import { useEffect, useRef, useState } from 'react';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

// useCountUp animates a number from 0 up to `target` on mount (and whenever
// `target` changes — so it also runs once async data arrives and the target
// flips from a 0 placeholder to the real value). Eases out cubically over
// `durationMs`. Honours prefers-reduced-motion by snapping straight to target,
// and starts already-settled for those users so there's no flash of 0.
export function useCountUp(target: number, durationMs = 900, decimals = 0): number {
  const [value, setValue] = useState(() => (prefersReducedMotion() ? target : 0));
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion() || !Number.isFinite(target)) {
      setValue(target);
      return;
    }
    const factor = Math.pow(10, decimals);
    let startTs: number | null = null;
    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const t = Math.min(1, (ts - startTs) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(Math.round(target * eased * factor) / factor);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    setValue(0);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs, decimals]);

  return value;
}
